/**
 * Active cross-filters per dashboard (S11) — EPHEMERAL, in-memory only
 * (pattern of dashboardRuntimeStore): a mark click is session state, unlike
 * param selections (uiState sidecar). `useTileResult` hashes the tile's
 * relevant subset into its query key, so applying/clearing filters refetches
 * exactly the participating tiles.
 *
 * `promptedFields` records (dashboard, field) combinations the wiring prompt
 * already showed this session — first-click-only, never nagging.
 */
import { create } from 'zustand';
import type { ActiveDashboardFilter } from '../types/dashboardRuntime';
import { toggleFilters } from '../services/crossFilter';

type CrossFilterState = {
  byDashboard: Record<string, ActiveDashboardFilter[]>;
  promptedFields: Record<string, string[]>;
  /** Mark-click entry: toggle semantics (same mark clears, new mark replaces same-field). */
  applyMark: (dashboardId: string, incoming: ActiveDashboardFilter[]) => void;
  clearField: (dashboardId: string, dashboardField: string) => void;
  clearAll: (dashboardId: string) => void;
  markPrompted: (dashboardId: string, dashboardField: string) => void;
};

const NO_FILTERS: ActiveDashboardFilter[] = [];

export const useDashboardCrossFilterStore = create<CrossFilterState>((set) => ({
  byDashboard: {},
  promptedFields: {},
  applyMark: (dashboardId, incoming) =>
    set((s) => ({
      byDashboard: {
        ...s.byDashboard,
        [dashboardId]: toggleFilters(s.byDashboard[dashboardId] ?? NO_FILTERS, incoming),
      },
    })),
  clearField: (dashboardId, dashboardField) =>
    set((s) => ({
      byDashboard: {
        ...s.byDashboard,
        [dashboardId]: (s.byDashboard[dashboardId] ?? NO_FILTERS).filter(
          (f) => f.dashboardField.toLowerCase() !== dashboardField.toLowerCase(),
        ),
      },
    })),
  clearAll: (dashboardId) =>
    set((s) => ({ byDashboard: { ...s.byDashboard, [dashboardId]: NO_FILTERS } })),
  markPrompted: (dashboardId, dashboardField) =>
    set((s) => ({
      promptedFields: {
        ...s.promptedFields,
        [dashboardId]: [...(s.promptedFields[dashboardId] ?? []), dashboardField.toLowerCase()],
      },
    })),
}));

export const selectActiveFilters =
  (dashboardId: string) =>
  (s: CrossFilterState): ActiveDashboardFilter[] =>
    s.byDashboard[dashboardId] ?? NO_FILTERS;

export const wasPrompted = (dashboardId: string, dashboardField: string): boolean =>
  (useDashboardCrossFilterStore.getState().promptedFields[dashboardId] ?? []).includes(
    dashboardField.toLowerCase(),
  );
